import { Avatar, Chip, Skeleton } from "@heroui/react";
import { lazy, Suspense } from "react";

import { useSession } from "@/src/contexts/AuthContext";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { initials } from "@/src/libs/initials";
import { relativeTime } from "@/src/libs/relativeTime";
import { Eyebrow } from "@/src/pages/Dashboard/components/Eyebrow";
import { FeedPanel } from "@/src/pages/Dashboard/components/FeedPanel";
import { NewAccountsPanel } from "@/src/pages/Dashboard/components/NewAccountsPanel";
import { QueuePanel } from "@/src/pages/Dashboard/components/QueuePanel";
import { StatTile } from "@/src/pages/Dashboard/components/StatTile";
import { UpcomingPanel } from "@/src/pages/Dashboard/components/UpcomingPanel";
import { WatchBanner } from "@/src/pages/Dashboard/components/WatchBanner";
import { useAccountPulse } from "@/src/pages/Dashboard/hooks/useAccountPulse";
import { useEventPulse } from "@/src/pages/Dashboard/hooks/useEventPulse";
import { useFeedPulse } from "@/src/pages/Dashboard/hooks/useFeedPulse";
import { useModerationQueue } from "@/src/pages/Dashboard/hooks/useModerationQueue";
import { useWhoami } from "@/src/pages/Dashboard/hooks/useWhoami";
import { ROUTES } from "@/src/routes/paths";

/**
 * Les tendances, **chargées à la demande**.
 *
 * ⚠️ Recharts et ses dépendances pèsent une centaine de kilo-octets
 * compressés, pour six graphiques qui vivent sous la ligne de flottaison. Le
 * `lazy` les sort du lot initial : le bandeau de garde — la seule chose qui
 * presse — s'affiche sans les attendre. C'est aussi ce qui rend la dépendance
 * acceptable dans un dépôt qui refuse une bibliothèque d'icônes pour six
 * glyphes.
 */
const StatsSection = lazy(() =>
  import("@/src/pages/Dashboard/stats/StatsSection").then((module) => ({
    default: module.StatsSection,
  })),
);

/**
 * Le repli de `Suspense`, hors du composant : recréé à chaque rendu, il
 * remonterait le squelette pour rien — c'est ce que signale `react-perf`.
 */
const STATS_FALLBACK = <Skeleton aria-hidden className="h-72 rounded-lg" />;

/**
 * Écran d'accueil de l'admin (`/`).
 *
 * Il répond à une question, dans cet ordre : **qu'est-ce qui presse ?** (le
 * poste de garde et son échéance de 24 h), **où en est la plateforme ?** (quatre
 * chiffres), **et concrètement ?** (les trois listes qui les détaillent).
 *
 * ⚠️ Aucun compteur n'est calculé ici : `total` est un `count(*)` côté serveur,
 * indépendant de `limit`. Chaque hook demande donc une ou cinq lignes, jamais la
 * liste entière pour en mesurer la longueur.
 *
 * ⚠️ Il ne rend **ni titre, ni réglages, ni déconnexion** : tout ça vit dans la
 * coque (`AppLayout`). Un écran ne dessine que son contenu.
 */
export function Dashboard() {
  const t = useTranslations("dashboard");
  const { locale } = useLocale();

  const queue = useModerationQueue();
  const feed = useFeedPulse();
  const events = useEventPulse();
  const accounts = useAccountPulse();

  /*
    ⚠️ **Un seul toast pour quatre domaines.** L'écran tire d'un coup sur les
    signalements, le fil, les événements et les comptes : un toast par hook en
    afficherait quatre pour une seule panne, au même instant. Chaque panneau dit
    par ailleurs lui-même qu'il n'a rien pu lire — le toast explique, le panneau
    constate.
  */
  const isError =
    queue.isError || feed.isError || events.isError || accounts.isError;
  useQueryErrorToast(
    isError,
    t("load-error"),
    queue.isError ? queue.error : undefined,
  );

  const number = new Intl.NumberFormat(locale);
  const now = Date.now();

  // Un compteur reste `undefined` tant que **les deux** files ne sont pas
  // connues : une somme à moitié chargée afficherait un chiffre faux.
  const handled =
    queue.actioned === undefined || queue.dismissed === undefined
      ? undefined
      : queue.actioned + queue.dismissed;

  const newest = accounts.accounts[0];
  const nextGame = events.events[0];

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <Eyebrow>{t("watch-eyebrow")}</Eyebrow>
          <SignedInAs />
        </div>

        <WatchBanner
          isError={queue.isError}
          oldest={queue.reports[0]}
          pending={queue.pending}
        />
      </section>

      <section className="flex flex-col gap-3">
        <Eyebrow>{t("pulse-eyebrow")}</Eyebrow>

        {/* `min-w-0` est porté par la tuile elle-même : un item de grille a
            `min-width: auto`, et un long nombre élargirait sa colonne.

            ⚠️ **Cinq colonnes au plus large, trois au milieu.** À quatre, la
            tuile du fil tombait seule sur une seconde rangée — une orpheline
            qui se lit comme un ajout après coup plutôt que comme un cinquième
            chiffre de la plateforme. */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
          <StatTile
            hint={
              newest
                ? t("hint-last-signup", {
                    when: relativeTime(newest.createdAt, locale, now) ?? "",
                  })
                : null
            }
            href={ROUTES.accounts}
            label={t("tile-accounts")}
            value={accounts.total}
          />

          <StatTile
            hint={
              events.open === undefined
                ? null
                : t("hint-open-events", { count: number.format(events.open) })
            }
            href={ROUTES.events}
            label={t("tile-events")}
            value={events.total}
          />

          <StatTile
            hint={
              nextGame?.date
                ? t("hint-next-game", {
                    when: relativeTime(nextGame.date, locale, now) ?? "",
                  })
                : null
            }
            href={ROUTES.events}
            label={t("tile-upcoming")}
            value={events.upcoming}
          />

          {/*
            Le volume du fil, avec sa file en indice — même montage que la tuile
            des événements, où le total porte le nombre de parties encore
            ouvertes. Le chiffre dit où en est le fil, l'indice dit s'il faut
            s'y mettre.
          */}
          <StatTile
            hint={
              feed.pending === undefined
                ? null
                : t("hint-feed-pending", { count: number.format(feed.pending) })
            }
            href={ROUTES.posts}
            label={t("tile-feed")}
            value={feed.published}
          />

          <StatTile
            hint={
              queue.dismissed === undefined
                ? null
                : t("hint-dismissed", {
                    count: number.format(queue.dismissed),
                  })
            }
            href={ROUTES.reports}
            label={t("tile-handled")}
            value={handled}
          />
        </div>
      </section>

      {/*
        Trois colonnes de contenu sur deux colonnes de grille : la file de
        modération est plus large que ses voisines parce qu'elle porte deux
        lignes par entrée, et parce que c'est elle qu'on lit.

        ⚠️ `items-start` : sans lui, une colonne s'étire à la hauteur de
        l'autre, et une file vide se rend en un pavé blanc de sept cents pixels
        pour trois mots au milieu.
      */}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <QueuePanel
            isError={queue.isError}
            isLoading={queue.isLoading}
            reports={queue.reports}
          />

          {/*
            Les deux files de modération dans la même colonne, les deux listes
            de vie de la plateforme dans l'autre : c'est le découpage de la
            barre latérale (Modération / Communauté), et ce qu'on vient chercher
            en arrivant sur cet écran.
          */}
          <FeedPanel
            isError={feed.isError}
            isLoading={feed.isLoading}
            posts={feed.posts}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <UpcomingPanel
            events={events.events}
            isError={events.isError}
            isLoading={events.isLoading}
          />

          <NewAccountsPanel
            accounts={accounts.accounts}
            isError={accounts.isError}
            isLoading={accounts.isLoading}
          />
        </div>
      </div>

      {/*
        ⚠️ **Le repli réserve la hauteur** plutôt que de laisser un vide : sans
        lui, l'arrivée du lot de Recharts pousserait d'un coup tout ce qui suit,
        et la page sauterait sous les yeux de qui la lisait déjà.
      */}
      <Suspense fallback={STATS_FALLBACK}>
        <StatsSection />
      </Suspense>
    </div>
  );
}

/**
 * Qui est connecté — la preuve que la chaîne complète fonctionne.
 *
 * L'e-mail vient de `useWhoami`, jamais de la session stockée : c'est ce qui
 * atteste qu'un appel authentifié aboutit vraiment. Il tient sur une ligne, à
 * hauteur du surtitre : c'est un renseignement, pas une tâche.
 */
function SignedInAs() {
  const { session } = useSession();
  const { account, isLoading } = useWhoami();
  const t = useTranslations("dashboard");

  const roles = account?.roles ?? session?.roles ?? [];

  /* ⚠️ Un `div` et non un `p` : l'avatar et les puces de rôle sont des
     éléments de bloc, qu'un paragraphe ne peut pas contenir. */
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
      <Avatar className="size-5 shrink-0" size="sm">
        <Avatar.Fallback className="text-[10px]">
          {initials(session?.username ?? "")}
        </Avatar.Fallback>
      </Avatar>

      {t("signed-in-as")}

      {isLoading ? (
        <Skeleton className="h-4 w-44" />
      ) : (
        <span className="font-medium text-foreground">
          {account?.email ?? session?.email}
        </span>
      )}

      {roles.map((role) => (
        <Chip key={role} size="sm">
          {role}
        </Chip>
      ))}
    </div>
  );
}
